import { refDebounced } from "@vueuse/core";
import { useFetch } from "~vue/api";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";

type PublicProfilesResponse = {
    items: ExistingPublicProfile[];
    total: number;
};

export type PublicProfileSearchSource = "chat" | "my_people";

type PublicProfileSearchOptions = {
    initialSearchTerm?: string;
    source: PublicProfileSearchSource;
};

const PAGE_SIZE = 20;

export function usePublicProfileSearch({ initialSearchTerm = "", source }: PublicProfileSearchOptions) {
    const query = ref(initialSearchTerm);
    const debouncedQuery = refDebounced(query, 300);
    const offset = ref(0);
    const searchUrl = computed(() => `/api/accounts/profile/list/?q=${encodeURIComponent(debouncedQuery.value)}&offset=${offset.value}&limit=${PAGE_SIZE}`);

    const { data, error, isFetching, onFetchResponse } = useFetch(searchUrl, {
        immediate: !!initialSearchTerm.trim(),
        refetch: true,
    })
        .get()
        .json<PublicProfilesResponse>();

    // useFetch replaces `data` on every request, so pages have to be kept here to survive the next fetch.
    const accumulatedProfiles = ref<ExistingPublicProfile[]>([]);

    const hasQuery = computed(() => !!query.value.trim());
    const isDebouncing = computed(() => query.value !== debouncedQuery.value);
    const isLoadingMore = computed(() => offset.value > 0 && isFetching.value);
    const isSearching = computed(() => isDebouncing.value || (isFetching.value && offset.value === 0));
    const profiles = computed(() => (!hasQuery.value || isSearching.value || error.value ? [] : accumulatedProfiles.value));
    const total = computed(() => data.value?.total ?? 0);
    const hasMore = computed(() => hasQuery.value && accumulatedProfiles.value.length < total.value);
    const hasSearched = computed(() => data.value !== null && !error.value);
    const hasNoMatch = computed(() => hasSearched.value && !isSearching.value && !!query.value.trim() && profiles.value.length === 0);

    const loadMore = () => {
        if (isFetching.value || isDebouncing.value || !hasMore.value) {
            return;
        }
        offset.value += PAGE_SIZE;
    };

    // Synchronous so the offset is back to 0 before `refetch` reacts to the new debounced term,
    // otherwise a new search would first request whichever page the previous one had scrolled to.
    watch(
        debouncedQuery,
        () => {
            offset.value = 0;
            accumulatedProfiles.value = [];
        },
        { flush: "sync" },
    );

    onFetchResponse(() => {
        const items = data.value?.items ?? [];
        accumulatedProfiles.value = offset.value > 0 ? [...accumulatedProfiles.value, ...items] : items;
        void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_SEARCHED, { result_count: data.value?.total ?? 0, source });
    });

    watch(error, (value) => {
        if (value) {
            logError(`Collaboration profile search failed: ${value}`);
        }
    });

    return { query, profiles, error, isFetching: isSearching, hasNoMatch, total, hasMore, isLoadingMore, loadMore };
}
